// App shell after UCP/DWM (Figma UCP Patterns › DWMSidebar, DWM › Aurora layout):
// desktop ≥1000: 228 px sidebar, header (page title · Help center · account), Aurora footer.
// mobile/tablet: top bar (logo · account), page title, bottom tab bar with Footprint in the middle.
// /scan uses the focus layout: logo only, no navigation.
import { Divider } from '@aurora-reactkit/components/divider';
import { Footer, FooterContent, FooterCopyright, FooterLink, FooterLinkList } from '@aurora-reactkit/components/footer';
import { Link as AuroraLink } from '@aurora-reactkit/components/link';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Fragment, Suspense } from 'react';
import { Skeleton } from '../components/Skeleton';
import { NavLink, useLocation, useOutlet } from 'react-router-dom';
import { Icon } from '../icons';
import { useIsDesktop } from '../lib/useMediaQuery';
import { sharedAxis } from '../styles/motion';
import { AccountMenu } from './AccountMenu';
import { Logo } from './Logo';
import { LAB_NAV, RAIL_GROUPS, TAB_NAV, titleFor, type NavItem } from './nav';
import { useShowLab } from './labPrefs';
import { openDemoMenu } from './demoBus';
import s from './Shell.module.css';

function PageFallback() {
  return <div role="status" aria-busy="true" aria-label="Loading" className={s.fallback}><Skeleton height={160} /><Skeleton height={240} /></div>;
}

function AnimatedOutlet() {
  const outlet = useOutlet();
  const { pathname } = useLocation();
  const v = sharedAxis(useReducedMotion());
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div key={pathname} variants={v} initial="initial" animate="animate" exit="exit">
        {/* Route chunks load inside the shell, so the nav never disappears while a screen loads. */}
        <Suspense fallback={<PageFallback />}>{outlet}</Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

const SidebarItem = ({ item }: { item: NavItem }) => (
  <NavLink to={item.to} end={item.to === '/'} className={`bodyXsMedium ${s.railLink}`} data-testid={`nav-${item.id}`}>
    <Icon name={item.icon} size={16} />
    <span>{item.label}</span>
  </NavLink>
);

function AppFooter() {
  return (
    <Footer className={s.footer}>
      <FooterContent>
        <FooterLinkList>
          <FooterLink href="https://support.nordvpn.com" target="_blank" rel="noreferrer">Support Center</FooterLink>
          <FooterLink href="https://my.nordaccount.com/legal/terms-of-service/" target="_blank" rel="noreferrer">Terms of Service</FooterLink>
          <FooterLink href="https://my.nordaccount.com/legal/privacy-policy/" target="_blank" rel="noreferrer">Privacy Policy</FooterLink>
        </FooterLinkList>
        <FooterCopyright>© 2026 Nord Security. All Rights Reserved.</FooterCopyright>
      </FooterContent>
    </Footer>
  );
}

export function Shell() {
  const desktop = useIsDesktop();
  const { pathname } = useLocation();
  const title = titleFor(pathname);
  const showLab = useShowLab();
  return (
    <div className={s.shell} data-testid="app-shell" data-layout={desktop ? 'rail' : 'tabs'}>
      <a href="#main" className={s.skip}>Skip to content</a>
      {desktop ? (
        <aside className={s.rail}>
          <div className={s.railLogo}><Logo /></div>
          <nav aria-label="Main" className={s.railNav}>
            {RAIL_GROUPS.map((group, i) => (
              <Fragment key={i}>
                {i > 0 && <Divider className={s.railDivider} />}
                <div className={s.railGroup}>{group.map((item) => <SidebarItem key={item.id} item={item} />)}</div>
              </Fragment>
            ))}
          </nav>
          {/* Design exploration, kept out of the main navigation; the demo menu can hide it. */}
          {showLab && <nav aria-label="Footprint lab" className={s.railNav}>
            <Divider className={s.railDivider} />
            <div className={s.railGroup}>
              <NavLink to="/lab" end className={`bodyXsMedium ${s.railLabLabel}`} data-testid="nav-lab">Footprint lab</NavLink>
              {LAB_NAV.map((item) => <SidebarItem key={item.id} item={item} />)}
            </div>
          </nav>}
        </aside>
      ) : (
        <header className={s.topbar}>
          <Logo large />
          <AccountMenu placement="top" />
        </header>
      )}
      <div className={s.column}>
        {desktop ? (
          <header className={s.header}>
            <h1 className={s.title}>{title}</h1>
            <div className={s.headerActions}>
              <AuroraLink href="https://support.nordvpn.com" target="_blank" rel="noreferrer" variant="neutral" className="bodyXsMedium" iconStart={<Icon name="circle-question" size={16} />}>
                Help center
              </AuroraLink>
              <button type="button" className={s.demoBtn} onClick={openDemoMenu} data-testid="header-demo" aria-label="Demo menu" title="Demo menu (⌘.)">
                <Icon name="flask" size={16} />
              </button>
              <AccountMenu placement="header" />
            </div>
          </header>
        ) : (
          <div className={s.mobileTitle}><h1 className={s.title}>{title}</h1></div>
        )}
        <main id="main" className={s.main} tabIndex={-1}>
          <div className={s.content}><AnimatedOutlet /></div>
        </main>
        <AppFooter />
      </div>
      {!desktop && (
        <nav aria-label="Main" className={s.tabbar}>
          {TAB_NAV.map((i) => (
            <NavLink key={i.id} to={i.to} end={i.to === '/'} className={s.tab} data-testid={`nav-${i.id}`}>
              <span className={s.tabIcon}><Icon name={i.icon} size={20} /></span>
              <span className={`body2xsMedium ${s.tabLabel}`}>{i.short}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}

export function FocusShell() {
  return (
    <div className={`${s.shell} ${s.focus}`} data-testid="app-shell" data-layout="focus">
      <header className={s.topbar}><Logo large /></header>
      <main id="main" className={s.main} tabIndex={-1}>
        <div className={`${s.content} ${s.contentNarrow}`}><AnimatedOutlet /></div>
      </main>
    </div>
  );
}
